/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { EditorPlugins } from '../../types/EditorPlugins';
import { getPluginByClassName } from '../getPluginByClassName/getPluginByClassName';

export function findRequiredPlugins( pluginName: string, editorPlugins: EditorPlugins ): EditorPlugins {
	const pluginsToLoad: Set<string> = new Set();

	const find = ( plugin: string ) => {
		getPluginByClassName( plugin )
			.requires
			.filter( requiredPlugin => filterCallback( { requiredPlugin, pluginsToLoad, plugin, editorPlugins } ) )
			.forEach( requirement => {
				// When processing a single requirement, process it normally.
				if ( !Array.isArray( requirement ) ) {
					pluginsToLoad.add( requirement );
					find( requirement );

					return;
				}

				// When processing an array and any of the required plugins is already scheduled to load, do nothing
				if ( requirement.some( plugin => pluginsToLoad.has( plugin ) ) ) {
					return;
				}

				// Otherwise, we load the first one.
				const firstRequiredPlugin = requirement.at( 0 )!;
				pluginsToLoad.add( firstRequiredPlugin );
				find( firstRequiredPlugin );
			} );
	};

	find( pluginName );

	return [ ...pluginsToLoad ];
}

type FilterCallbackArgs = {
	requiredPlugin: string | Array<string>;
	pluginsToLoad: Set<string>;
	plugin: string;
	editorPlugins: EditorPlugins;
};

function filterCallback( { requiredPlugin, pluginsToLoad, plugin, editorPlugins }: FilterCallbackArgs ): boolean {
	if ( !Array.isArray( requiredPlugin ) ) {
		return !pluginsToLoad.has( requiredPlugin );
	}

	// When processing an array, it means a plugin defines its requirements as a "one of".
	// The `plugin` will work properly using any of the `requiredPlugins` dependencies.
	//
	// Hence, the function tries to verify if there is anything to load.
	// Before we start, check whether the processed plugin isn't already marked as a plugin to load.
	if ( pluginsToLoad.has( plugin ) ) {
		return false;
	}

	// Also, do not process the plugin when "the current item" is included in the "one of" requirement.
	// The `@ckeditor/ckeditor5-metadata` package exports CKEditor 5 metadata combined into a single file.
	// Each plugin contains (deeply) requirements of its dependencies.
	//
	// Let's consider the following plugins tree as an example:
	//
	// ```
	// Alpha
	// ├── Beta
	// └── Gamma
	//
	// Beta
	// └── Delta
	//
	// Delta
	// └── Epsilon
	//
	// Epsilon
	// ```
	//
	// The calculated dependency tree that the function receives looks like:
	//
	// ```
	// Alpha
	// ├── Beta
	// ├── Delta
	// ├── Gamma
	// └── Epsilon
	//
	// Beta
	// ├── Delta
	// └── Epsilon
	//
	// Delta
	// └── Epsilon
	//
	// Epsilon
	// ```
	//
	// It might happen that the `plugin` (due to various scenarios) may depend on itself.
	// In such case, to avoid circular dependency, we do not allow to loading the same plugin twice.
	if ( requiredPlugin.includes( plugin ) ) {
		return false;
	}

	// Also, do not process the requirements if one of the required plugin is already loaded.
	return !requiredPlugin.some( item => editorPlugins.includes( item ) );
}
